import { BaseFilterComponent } from './types';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Filter } from '../../types';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { GeneSetsFilterOption } from '../options/types';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import { useEffectOnce } from 'react-use';
import * as React from 'react';
import KnowledgeService, {
  GeneSet,
} from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';

const knowledgeService = new KnowledgeService();

interface GeneSetSelectionProps
  extends GeneSetsFilterOption,
    BaseFilterComponent {}

export const GeneSetSelection: React.FC<GeneSetSelectionProps> = ({
  injectedProps: { searchKey },
  onFilterAdd,
  onFilterRemove,
  origin,
  selectedFilterOptions,
}) => {
  const activeProjectId = useActiveProjectId();

  const [fetchGeneSets, fetchGeneSetsState] = useService(
    knowledgeService.getGeneSets,
  );

  const [checkedState, setCheckedState] = React.useState<
    Record<string, boolean>
  >({});
  const [geneSets, setGeneSets] = React.useState<Array<GeneSet>>([]);

  useEffectOnce(() => {
    fetchGeneSets(activeProjectId);
  });

  React.useEffect(() => {
    if (!fetchGeneSetsState?.response?.data) {
      return;
    }

    setGeneSets(fetchGeneSetsState?.response?.data?.items);
  }, [fetchGeneSetsState]);

  // Handle case where an item is de-selected from BagOfChips
  // instead of manually un-checked
  React.useEffect(() => {
    const checkedKeys = Object.keys(checkedState);

    checkedKeys?.forEach(k => {
      const state = Boolean(selectedFilterOptions?.find(o => o.id === k));
      const checkedVal = checkedState[k];

      if (checkedVal !== state) {
        checkedState[k] = state;
      }
    });
  }, [checkedState, selectedFilterOptions]);

  const makeChangeHandler =
    (geneSet: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.checked;

      setCheckedState({ ...checkedState, [geneSet.id]: e.target.checked });

      const clonedGeneSet: Filter = {
        friendlyName: geneSet.name,
        id: geneSet.id,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        origin,
        searchKey,
        searchValue: geneSet.genes.map((g: any) => g.gene).join(','),
      };

      if (value) {
        onFilterAdd?.(clonedGeneSet);
      }

      if (!value) {
        onFilterRemove?.(clonedGeneSet);
      }
    };

  return (
    <FormBox spacing={1}>
      {fetchGeneSetsState.isFetching && <DotLoader size={0} />}
      {geneSets?.map(geneSet => {
        return (
          <Checkbox
            key={geneSet.id}
            label={geneSet.name}
            checked={Boolean(checkedState[geneSet.id])}
            onChange={makeChangeHandler(geneSet)}
          />
        );
      })}
    </FormBox>
  );
};
